import React from 'react';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import {
    destroyChallenge,
    getChallengeData,
    startChallenge,
    updateChallengerLanguage,
} from '../../../../src/utils/challengerMain';
import PrivacyCenter from '../PrivacyCenter';
import Spinner from '../../../app/common/components/Spinner';
import SwitchToggleButton from '../../../app/common/components/SwitchToggleButton';
import { ContactLink } from '../contactLink/ContactLink';
import ServiceConsentCard from '../serviceConsentCard/ServiceConsentCard';
import {
    Bottom,
    BottomEUService,
    BottomEUServiceLogo,
    BottomEUServiceTitle,
    BottomUCRMDescription,
    BottomUCRMTitle,
    DataSharingPageLink,
    ImgLogo,
    Loading,
    TopDescription,
    Wrapper,
} from '../sharedStyles';
import {
    CheckAgreement,
    CheckAgreementLink,
    CheckAgreementText,
    OptionalConsent,
    OptionalConsentContent,
    SwitchToggleButtonContainer,
} from './PrivacyCenter.styles';
import { createRatEventForClick } from './UCRMRat';
import { SocialAccountRatEvents } from '../../socialAccounts/utils/socialAccountRatEvents';
import HeadTitle from '../../../app/common/components/HeadTitle';
import OverlayHelper from '../../../utils/overlayHelper';
import DsrDeletionOverlay from '../dsr/DsrDeletionOverlay';
import UnlinkConfirmationModal from '../../../app/common/components/UnlinkConfirmationModal';
import { PageId } from '../../../config/constants/pageId.enum';

export default class UCRM extends PrivacyCenter {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            clientId: null,
            isScrolled: false,
            activeServiceName: null,
        };
        this.itemRefs = {};
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.loadRecommendedEUServices();
        this.setClientId();
        const { challengerPid } = this.props;
        if (challengerPid) {
            startChallenge(challengerPid);
        }
    }

    setClientId() {
        const clientIdFromUrl = new URL(window.location.href).hash;
        if (clientIdFromUrl) {
            this.setState({
                clientId: clientIdFromUrl.slice(1),
            });
        } else {
            const activeClientId = webStorage.getActiveClientId();
            this.setState({
                clientId: activeClientId,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.state.isScrolled && this.itemRefs) {
            this.scrollToService(this.state.clientId);
        }
        if (this.props.locale !== prevProps.locale) {
            updateChallengerLanguage(this.props.locale);
        }
    }

    scrollToService(clientId) {
        const {
            services,
            gettingServicesAndConsentsStatus,
            loadingServicesConfig,
            loadingServicesConfigError,
        } = this.props;
        const someServiceUpdating = services.some((service) => service.updating);
        const isServiceLoading =
            gettingServicesAndConsentsStatus ||
            loadingServicesConfig ||
            someServiceUpdating ||
            loadingServicesConfigError;
        if (this.itemRefs[clientId] && !isServiceLoading) {
            this.setState({ isScrolled: true }, () =>
                this.itemRefs[clientId].current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                }),
            );
        }
    }

    componentWillUnmount() {
        const { challengerPid } = this.props;
        destroyChallenge(challengerPid);
        OverlayHelper.closeHelper(PageId.ucrm);
    }

    extractMandatoryConsents(mandatoryConsents) {
        return mandatoryConsents.map((consent, i) => {
            const translationKey = 'rakservice.check.' + consent.translationKey;
            return (
                <CheckAgreement key={consent.translationKey}>
                    <CheckAgreementText>
                        {TranslationHelpers.getFormattedMessage('rakservice.check.check')}{' '}
                    </CheckAgreementText>
                    <CheckAgreementLink
                        href={consent.url}
                        key={i}
                        onClick={(e) =>
                            this.onExternalLinkClick(e, {
                                page: 'Privacy',
                                destination: this.getAnalyticsDestination(consent.translationKey),
                            })
                        }
                    >
                        {TranslationHelpers.getFormattedMessage(translationKey)}
                    </CheckAgreementLink>
                </CheckAgreement>
            );
        });
    }

    extractOptionalConsents(optionalConsents, serviceUuid) {
        const service = this.props.services.find((s) => s.serviceUuid === serviceUuid);
        const isUpdating = service?.updating || false;
        return optionalConsents.map((consent, i) => {
            const { extraParameters, title, content, translationKey } = consent;
            return (
                <React.Fragment key={`${serviceUuid}-${i}`}>
                    <OptionalConsent className="tst-nslt">
                        <p>
                            {' '}
                            {title ? title : TranslationHelpers.getFormattedMessage(translationKey)}
                        </p>
                        <SwitchToggleButtonContainer>
                            <SwitchToggleButton
                                value={consent.granted}
                                disabled={isUpdating}
                                onSwitch={() => {
                                    this.props.updateOptionalConsentStatus(
                                        {
                                            extraParameters,
                                            serviceUuid,
                                        },
                                        this.props.locale,
                                    );
                                }}
                            />
                        </SwitchToggleButtonContainer>
                    </OptionalConsent>
                    <OptionalConsentContent>{content}</OptionalConsentContent>
                </React.Fragment>
            );
        });
    }

    extractRecommendedEUServices() {
        const { recommendations, loadingServiceRecommendations } = this.props;

        if (loadingServiceRecommendations) {
            return (
                <Loading>
                    <Spinner size={50} />
                </Loading>
            );
        } else {
            if (recommendations && recommendations.length > 0) {
                return (
                    <BottomEUService>
                        <BottomEUServiceTitle>
                            {TranslationHelpers.getFormattedMessage('ucrm.bottom.eu.services')}
                        </BottomEUServiceTitle>

                        {recommendations.map((service, index) => {
                            const { uuid, logo, name, link } = service;
                            return (
                                <BottomEUServiceLogo key={`${uuid}-${index}`}>
                                    <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            createRatEventForClick(name, link);
                                        }}
                                    >
                                        <ImgLogo src={logo} alt={name} />
                                    </a>
                                </BottomEUServiceLogo>
                            );
                        })}
                    </BottomEUService>
                );
            }
        }
    }

    displayServicesAndConsents(services, cmpIntegratedFlag, anyServiceCmpIntegrated) {
        return services
            .filter((service) => service.cmpIntegrated === cmpIntegratedFlag)
            .map((service) => {
                const serviceDetails = this.getServiceDetailsByConfig(service);

                if (
                    !serviceDetails.name ||
                    serviceDetails.serviceUuid === this.getServiceUuidByMyrClientId()
                ) {
                    return null;
                }

                const mandatoryConsents =
                    service.mandatoryConsents &&
                    this.extractMandatoryConsents(service.mandatoryConsents);

                const optionalConsents =
                    service.optionalConsents &&
                    service.optionalConsents.consents &&
                    this.extractOptionalConsents(
                        service.optionalConsents.consents,
                        service.serviceUuid,
                    );

                const serviceRef = React.createRef();
                if (serviceDetails.myrClientId) {
                    this.itemRefs[serviceDetails.myrClientId] = serviceRef;
                }

                const dsrStatus = this.props.dsrStatuses?.find(
                    (el) => el.serviceUuid === serviceDetails.serviceUuid,
                );
                const unlinkTargetId = serviceDetails.name || serviceDetails.serviceUuid;

                return (
                    <div ref={serviceRef} key={serviceDetails.serviceUuid}>
                        <ServiceConsentCard
                            service={serviceDetails}
                            appConfig={this.props.appConfig}
                            mandatoryConsents={mandatoryConsents}
                            optionalConsents={optionalConsents}
                            onExternalLinkClick={this.onExternalLinkClick}
                            anyServiceCmpIntegrated={anyServiceCmpIntegrated}
                            dsrStatus={dsrStatus}
                            openDSRForm={this.props.openDSRForm}
                            showDsrDeleteStatusInfo={
                                this.props.showDSRDeleteStatusInfo[serviceDetails.serviceUuid]
                            }
                            toggleDsrDeleteStatusInfo={this.props.toggleDSRDeleteStatusInfo}
                            onUnlinkSocialProvider={(providerId, providerName) => {
                                this.setState({ activeServiceName: serviceDetails.name });
                                this.props.openSocialUnlinkForm(
                                    providerId,
                                    providerName,
                                    unlinkTargetId,
                                );
                            }}
                            socialUnlinkFormOpen={this.props.socialUnlinkFormOpen}
                            activeSocialProviderName={this.props.activeSocialProviderName}
                            onCloseSocialUnlinkForm={this.props.closeSocialUnlinkForm}
                            onConfirmSocialUnlink={() =>
                                this.props.unlinkSocialProvider(
                                    this.props.activeSocialProvider,
                                    this.props.activeSocialProviderName,
                                    this.props.activeSocialUnlinkTargetId,
                                )
                            }
                            unlinkingSocial={this.props.unlinkingSocial}
                        />
                    </div>
                );
            });
    }

    dsrSubmit = (password, serviceUuid) => {
        const body = {
            challengeData: getChallengeData(this.props.appConfig.CHALLENGER_PIDS.PRIVACY),
            serviceUuid,
        };
        this.props.submitDsrRequest(body, password);
    };

    displayTopTitle(anyServiceCmpIntegrated) {
        if (anyServiceCmpIntegrated) {
            return (
                <TopDescription>
                    <BottomUCRMTitle>
                        {TranslationHelpers.getFormattedMessage('ucrm.top.title')}
                    </BottomUCRMTitle>
                    <BottomUCRMDescription>
                        <p>
                            {TranslationHelpers.getFormattedMessage('ucrm.top.description_1')}
                            <DataSharingPageLink
                                href={'/' + this.props.locale + '/data-sharing.html'}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {TranslationHelpers.getFormattedMessage('ucrm.top.description_2')}
                            </DataSharingPageLink>
                            {TranslationHelpers.getFormattedMessage('ucrm.top.description_3')}
                        </p>
                    </BottomUCRMDescription>
                </TopDescription>
            );
        } else return null;
    }

    displayBottomTitle(anyServiceOnlyRidIntegrated, anyServiceCmpIntegrated) {
        if (anyServiceOnlyRidIntegrated) {
            if (anyServiceCmpIntegrated) {
                return (
                    <BottomUCRMTitle>
                        {TranslationHelpers.getFormattedMessage('ucrm.other.linked.services')}
                    </BottomUCRMTitle>
                );
            } else {
                return (
                    <BottomUCRMTitle>
                        {TranslationHelpers.getFormattedMessage('ucrm.your.linked.services')}
                    </BottomUCRMTitle>
                );
            }
        } else return null;
    }

    displaySupportUrlOrEmail(supportPageUrl) {
        if (supportPageUrl.startsWith('mailto:')) {
            return supportPageUrl.split(':')[1];
        } else return supportPageUrl;
    }

    render() {
        const {
            services,
            servicesConfig,
            gettingServicesAndConsentsStatus,
            loadingServicesConfig,
            loadingServicesConfigError,
            dsrFormOpen,
        } = this.props;

        const someServiceUpdating = services.some((service) => service.updating);
        const isServiceLoading =
            gettingServicesAndConsentsStatus ||
            loadingServicesConfig ||
            someServiceUpdating ||
            loadingServicesConfigError;

        // Removing deduplicated check for service config
        const hasServicesConfig = services && servicesConfig && !loadingServicesConfigError;

        /**
         * @todo - we should create a method that will evaluate the below checks
         * and check for both `anyServiceCmpIntegraded` and `anyServiceOnlyRidIntegrated` in one loop;
         */

        const anyServiceCmpIntegrated =
            hasServicesConfig &&
            services
                .filter((service) => service.cmpIntegrated === true)
                .map((service) => this.getServiceDetailsByConfig(service))
                .some((service) => service.name);

        const anyServiceOnlyRidIntegrated =
            hasServicesConfig &&
            services
                .filter((service) => service.cmpIntegrated === false)
                .map((service) => this.getServiceDetailsByConfig(service))
                .some((service) => service.name);

        return (
            <Wrapper>
                {isServiceLoading ? (
                    <Loading>
                        <Spinner size={50} />
                    </Loading>
                ) : null}
                <HeadTitle />
                {hasServicesConfig && this.displayTopTitle(anyServiceCmpIntegrated)}
                {hasServicesConfig &&
                    this.displayServicesAndConsents(services, true, anyServiceCmpIntegrated)}
                {hasServicesConfig &&
                    this.displayBottomTitle(anyServiceOnlyRidIntegrated, anyServiceCmpIntegrated)}
                {hasServicesConfig &&
                    this.displayServicesAndConsents(services, false, anyServiceCmpIntegrated)}
                {dsrFormOpen && (
                    <DsrDeletionOverlay
                        showDSRForm={this.props.dsrFormOpen}
                        closeDSRForm={this.props.closeDSRForm}
                        onAccept={(password) =>
                            this.dsrSubmit(password, this.props.activeServiceUuid)
                        }
                        deleting={this.props.deleting}
                        challengerPid={this.props.challengerPid}
                    />
                )}
                {this.props.socialUnlinkFormOpen && this.props.activeSocialProviderName && (
                    <UnlinkConfirmationModal
                        isOpen={this.props.socialUnlinkFormOpen}
                        providerName={this.props.activeSocialProviderName}
                        serviceName={this.state.activeServiceName || 'Rakuten'}
                        onClose={() => {
                            SocialAccountRatEvents.trackUnlinkCancel(
                                this.props.activeSocialProviderName,
                                this.props.activeSocialUnlinkTargetId,
                            );
                            this.props.closeSocialUnlinkForm();
                        }}
                        onConfirm={() =>
                            this.props.unlinkSocialProvider(
                                this.props.activeSocialProvider,
                                this.props.activeSocialProviderName,
                                this.props.activeSocialUnlinkTargetId,
                            )
                        }
                        unlinking={this.props.unlinkingSocial}
                    />
                )}
                <Bottom>
                    {this.extractRecommendedEUServices()}
                    <ContactLink
                        locale={this.props.locale}
                        supportLink={this.state.supportPageUrl}
                    />
                </Bottom>
            </Wrapper>
        );
    }
}
